import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'

import { Col, Layout, Row } from 'antd'
import { Formik } from 'formik'
import * as Yup from 'yup'

import { ForgotPasswordValues, saveForgotPasswordValue } from 'core/application/account/forgetPassword'
import { ProductIds } from 'core/application/commons/config'

import Input from 'common/components/dataEntry/formik/FormikStyledInputField'
import Alert from 'common/components/feedback/Alert'
import Button from 'common/components/general/Button'

import { commandRequest } from 'common/RequestUtils'

import useQueryParams from '../../common/hooks/useQueryParams'
import { ROUTE_PATHS } from '../../common/routePaths'

const formValidator = (translate: any) =>
  Yup.object({
    email: Yup.string()
      .email(translate('admin-login.errors.valid-email'))
      .required(translate('admin-login.errors.required-email')),
  })

interface FormValues {
  email: string
}

const initialValues: FormValues = {
  email: '',
}

const ForgetPasswordComponent: React.FC<FormValues> = () => {
  const { t: translate } = useTranslation()
  const [errors, setErrors] = useState<string[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const history = useHistory()
  const { emailValue } = useParams() as any
  const params = useQueryParams()
  const productId = params.get('productId') ?? ProductIds.GroupHraId

  useEffect(() => {
    if (emailValue) {
      initialValues.email = emailValue
    }
  }, [emailValue])

  const errorDescription = (email: string) => (
    <>
      <span>{translate('admin-login.forgot-password-email-alert').replace('[email]', email)}</span>
    </>
  )

  const onSubmit = async (values: FormValues) => {
    setLoading(true)
    let dataForSubmit: ForgotPasswordValues = {
      email: values.email,
    }
    let result = await commandRequest(() => saveForgotPasswordValue(dataForSubmit))

    if (!result.errors) {
      history.push(`${ROUTE_PATHS.FORGOT_PASSWORD_PATH}/sent/${values.email}?productId=${productId}`)
    } else {
      setErrors(result.errors)
    }
    setLoading(false)
  }

  return (
    <Layout>
      <Formik
        enableReinitialize={true}
        validationSchema={formValidator(translate)}
        initialValues={initialValues}
        onSubmit={onSubmit}
      >
        {(formik) => (
          <form onSubmit={formik.handleSubmit}>
            <Row>
              <Col span={24}>
                <Row>
                  <Col
                    sm={{ span: 24, offset: 3 }}
                    xs={{ span: 24 }}
                    md={{ span: 18, offset: 5 }}
                    lg={{ span: 15, offset: 7 }}
                    xl={{ span: 10, offset: 7 }}
                  >
                    <div className="pl-12">
                      <h3>{translate('admin-login.forgot-password')}</h3>
                      <p className="font-16">{translate('admin-login.forgot-password-instructions')}</p>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col
                    sm={{ span: 24, offset: 3 }}
                    xs={{ span: 24 }}
                    md={{ span: 18, offset: 5 }}
                    lg={{ span: 15, offset: 7 }}
                    xl={{ span: 10, offset: 7 }}
                  >
                    {errors.length > 0 && (
                      <Alert
                        type="error"
                        message={translate('admin-login.email-password-alert')}
                        description={errorDescription(formik.values.email)}
                        showIcon
                      />
                    )}
                  </Col>
                </Row>
                <Row className="form__row mt-36">
                  <Col
                    sm={{ span: 23, offset: 3 }}
                    xs={{ span: 24 }}
                    md={{ span: 18, offset: 5 }}
                    lg={{ span: 15, offset: 7 }}
                    xl={{ span: 10, offset: 7 }}
                  >
                    <Input label={translate('admin-login.labels.email')} name="email"></Input>
                  </Col>
                </Row>
                <Row className="form__row">
                  <div className="pl-12 full-width">
                    <Col
                      sm={{ span: 23, offset: 3 }}
                      xs={{ span: 24 }}
                      md={{ span: 18, offset: 5 }}
                      lg={{ span: 15, offset: 7 }}
                      xl={{ span: 10, offset: 7 }}
                      className="login--login-button"
                    >
                      <Button htmlType="submit" type="primary" className="button__large" loading={loading}>
                        {translate('admin-login.send-instructions-button')}
                      </Button>
                    </Col>
                  </div>
                </Row>
              </Col>
            </Row>
          </form>
        )}
      </Formik>
    </Layout>
  )
}

export default ForgetPasswordComponent
